import type { TFunction } from 'i18next'

import { ActorTypeEnum, SeverityEnum } from 'src/api/main/types.gen'
import type { OrganizationsAuditLogsListData } from 'src/api/main/types.gen'

/**
 * Locales the audit-log endpoints accept for `Accept-Language` and key the
 * localized `descriptions` map. Derived from the generated client so it stays
 * in sync if the backend adds a locale.
 */
export type AuditAcceptLanguage = NonNullable<
  NonNullable<OrganizationsAuditLogsListData['headers']>['Accept-Language']
>

const SUPPORTED_AUDIT_LANGUAGES: readonly AuditAcceptLanguage[] = [
  'en',
  'it',
  'ro',
  'ru'
]

/**
 * Normalize an app locale (e.g. `en-US`, `ro_RO`) to a supported audit locale,
 * falling back to English for anything the backend doesn't accept.
 */
export const toAuditAcceptLanguage = (
  language: string
): AuditAcceptLanguage => {
  const base = language.toLowerCase().split(/[-_]/)[0]

  return SUPPORTED_AUDIT_LANGUAGES.includes(base as AuditAcceptLanguage)
    ? (base as AuditAcceptLanguage)
    : 'en'
}

type AuditActorLike =
  | {
      id?: unknown
      email?: unknown
      full_name?: unknown
    }
  | null
  | undefined

const AUDIT_ACTION_KEY_ALIASES: Record<string, string> = {
  firebase: 'account_login_firebase',
  login_by_firebase: 'account_login_firebase',
  credentials: 'account_login_credentials',
  login_by_credentials: 'account_login_credentials',
  google_login: 'account_login_google',
  google_register: 'account_register_google',
  entraid_login: 'account_login_entraid',
  login_by_entraid: 'account_login_entraid',
  email_otp_login: 'account_login_email_otp',
  login_by_email_otp: 'account_login_email_otp',
  refresh_token: 'account_token_refresh',
  token_sent: 'account_token_sent',
  token_confirmed: 'account_token_confirmed',
  register: 'account_register',
  invited: 'account_invited',
  logout: 'account_logout',
  session_revoked: 'account_session_revoked',
  session_revoke_all: 'account_session_revoke_all',
  session_revoked_all: 'account_session_revoke_all',
  changed_password: 'account_password_changed',
  mfa_login: 'account_login_mfa',
  mfa_change_password: 'account_password_mfa_changed',
  otp_sent_success: 'account_mfa_otp_sent',
  otp_sent_fail: 'account_mfa_otp_sent_fail',
  otp_verify_success: 'account_mfa_otp_verified',
  otp_verify_fail: 'account_mfa_otp_verify_fail',
  account_mfa_otp_sent_success: 'account_mfa_otp_sent',
  account_mfa_otp_verify_success: 'account_mfa_otp_verified',
  backup_code_verify_success: 'account_mfa_backup_code_verified',
  backup_code_verify_fail: 'account_mfa_backup_code_verify_fail',
  account_mfa_backup_code_verify_success: 'account_mfa_backup_code_verified',
  trusted_device_created: 'account_mfa_trusted_device_added',
  trusted_device_deleted: 'account_mfa_trusted_device_removed',
  trusted_devices_deleted_all: 'account_mfa_trusted_devices_cleared',
  account_mfa_trusted_device_created: 'account_mfa_trusted_device_added',
  account_mfa_trusted_device_deleted: 'account_mfa_trusted_device_removed',
  account_mfa_trusted_devices_deleted_all:
    'account_mfa_trusted_devices_cleared',
  account_locked: 'account_mfa_account_locked',
  mfa_enabled: 'account_mfa_enabled',
  mfa_disabled: 'account_mfa_disabled',
  backup_codes_regenerated: 'account_mfa_backup_codes_regenerated',
  passkey_registered: 'account_passkey_registered',
  passkey_deleted: 'account_passkey_deleted',
  passkey_renamed: 'account_passkey_renamed',
  passkey_auth_success: 'account_passkey_auth_success',
  passkey_auth_fail: 'account_passkey_auth_fail',
  account_login_entra_id: 'account_login_entraid'
}

const AUDIT_CODE_WORDS: Record<string, string> = {
  api: 'API',
  crm: 'CRM',
  hrm: 'HRM',
  id: 'ID',
  ip: 'IP',
  mfa: 'MFA',
  oauth: 'OAuth',
  oidc: 'OIDC',
  otp: 'OTP',
  scim: 'SCIM',
  sso: 'SSO',
  url: 'URL',
  entraid: 'Entra ID'
}

export const toDisplayString = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : ''

/**
 * Returns the matching severity, or `null` when the value is missing/unknown —
 * the backend leaves severity unset for some events, so we render it as "no
 * severity" rather than fabricating a level.
 */
export function normalizeAuditSeverity(
  value?: string | null
): SeverityEnum | null {
  if (typeof value !== 'string') return null

  const lowered = value.trim().toLowerCase() as SeverityEnum
  const severities = Object.values(SeverityEnum)

  return severities.includes(lowered) ? lowered : null
}

/**
 * Single source of truth for which raw code identifies an audit event across
 * the table tag, the description label and the detail page title. All three
 * call-sites used to disagree (action vs event_type vs entity_type_action),
 * which produced different labels for the same record.
 */
export function getAuditEventCode(log: {
  event_type?: string | null
  entity_type?: string | null
  action?: string | null
}): string {
  if (log.event_type) return log.event_type

  if (log.entity_type && log.action) {
    return `${log.entity_type}_${log.action}`
  }

  return log.action || log.entity_type || ''
}

export const getAuditActorName = (
  actor: AuditActorLike,
  fallback = ''
): string =>
  toDisplayString(actor?.full_name) ||
  toDisplayString(actor?.email) ||
  toDisplayString(actor?.id) ||
  fallback

export function auditActionKey(action: string): string {
  const key = action
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()

  return AUDIT_ACTION_KEY_ALIASES[key] ?? key
}

export function humanizeAuditCode(value: string): string {
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
      const lower = word.toLowerCase()
      const override = AUDIT_CODE_WORDS[lower]
      if (override) return override

      return lower.charAt(0).toUpperCase() + lower.slice(1)
    })
    .join(' ')
}

/** Maps mock/API audit codes like `account.session.revoked` to nested i18n keys. */
export function auditActionTranslationKey(action: string): string {
  return `audit.actions.${auditActionKey(action)}`
}

export function translateAuditAction(t: TFunction, action: string): string {
  const fallback = humanizeAuditCode(action)
  if (!fallback) return ''

  return t(auditActionTranslationKey(action), { defaultValue: fallback })
}

export function translateAuditSeverity(t: TFunction, severity: string): string {
  return t(`audit.severity.${severity}`, { defaultValue: severity })
}

export function translateAuditActorType(
  t: TFunction,
  actorType: string
): string {
  // A value added to the enum server-side lands here before it has a translation;
  // humanize keeps it readable instead of showing the raw code.
  return t(`audit.actorType.${actorType}`, {
    defaultValue: humanizeAuditCode(actorType)
  })
}

/**
 * What to call an event that carries no actor. Only `system` legitimately has none —
 * users, agents and API keys all bring one — so anything else stays blank rather
 * than borrowing a filter label, which is worded in the plural.
 */
export function getActorlessLabel(
  actorType: string | null | undefined,
  t: TFunction
): string {
  return actorType === ActorTypeEnum.SYSTEM
    ? translateAuditActorType(t, ActorTypeEnum.SYSTEM)
    : ''
}

export function translateAuditScope(
  t: TFunction,
  scope: string | undefined
): string | undefined {
  if (scope == null || scope === '') {
    return undefined
  }
  return t(`audit.scope.${scope}`, { defaultValue: scope })
}
